import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiCheck, FiX, FiEye, FiAlertTriangle, FiFileText } from 'react-icons/fi';
import { where, orderBy, limit } from 'firebase/firestore';
import {
  Card,
  Table,
  Badge,
  Button,
  Modal,
  Input,
  Pagination,
  CsvExportButton,
  BulkActionBar,
} from '../../components/UI';
import { useFirestoreCollection, usePagination, useUserDirectory } from '../../hooks';
import { moderateReport, dismissReport } from '../../services/adminService';
import { useAuth } from '../../context/AuthContext';
import { exportReportToPdf } from '../../utils/reportPdf';
import styles from './Reports.module.css';

// There are TWO distinct report collections in production, and this page used
// to read the wrong one (verified 2026-08-21):
//
//   content_reports — the moderation queue. Written by the app's "Report"
//     menu on a post or a profile:
//       { postId, postType:'post'|'user', type, reason, reportedId,
//         reportedUserId, reportedByUserId, status:'pending'|…, createdAt }
//     This is the collection adminModerateReport / adminDismissReport act on
//     (functions/index.js — `db.collection('content_reports').doc(reportId)`).
//
//   reports — animal-welfare / emergency reports filed from ReportScreen:
//       { type:'EMERGENCY', urgency, status:'Submitted', description, location,
//         coordinates, images[], mainImage, referenceId, contactEmail,
//         contactPhone, recipient, recipientName, pdfReport, userId, createdAt }
//     Nothing moderates these; they are a case file with photos and a PDF.
//
// The page previously listed `reports` while its Resolve/Dismiss buttons called
// a Cloud Function that looks the id up in `content_reports` — so every action
// failed with not-found, and the real moderation queue was invisible. It also
// defaulted to status 'pending', which no welfare report ever has (they are
// 'Submitted'), so the tab rendered empty.
const SOURCES = {
  moderation: {
    collection: 'content_reports',
    label: 'Content moderation',
    // Statuses written by adminModerateReport.
    statusOptions: [
      { value: 'pending', label: 'Pending' },
      { value: 'resolved', label: 'Resolved' },
      { value: 'dismissed', label: 'Dismissed' },
      { value: 'all', label: 'All' },
    ],
    typeOptions: [
      { value: 'all', label: 'All Types' },
      { value: 'user', label: 'User' },
      { value: 'post', label: 'Post' },
      { value: 'business', label: 'Business' },
      { value: 'message', label: 'Message' },
    ],
    actionable: true,
  },
  welfare: {
    collection: 'reports',
    label: 'Welfare / emergency',
    statusOptions: [
      { value: 'all', label: 'All' },
      { value: 'Submitted', label: 'Submitted' },
      { value: 'In Progress', label: 'In progress' },
      { value: 'Closed', label: 'Closed' },
    ],
    typeOptions: [
      { value: 'all', label: 'All Types' },
      { value: 'EMERGENCY', label: 'Emergency' },
      { value: 'ABUSE', label: 'Abuse' },
      { value: 'NEGLECT', label: 'Neglect' },
    ],
    actionable: false,
  },
};

/** Reporter uid differs per collection: content_reports uses reportedByUserId. */
const reporterUid = (r) => r?.reportedByUserId || r?.userId || '';

/** content_reports has `reason`; welfare reports carry a free-text description. */
const reportReason = (r) => r?.reason || r?.description || '';

const formatDate = (value) => {
  if (!value) return '-';
  const d = value.toDate ? value.toDate() : new Date(value);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString();
};

const Reports = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [source, setSource] = useState(searchParams.get('source') === 'welfare' ? 'welfare' : 'moderation');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'pending');
  const [typeFilter, setTypeFilter] = useState('all');
  const cfg = SOURCES[source];
  const [selectedReport, setSelectedReport] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');

  const constraints = useMemo(() => {
    const c = [];
    if (statusFilter !== 'all') c.push(where('status', '==', statusFilter));
    if (typeFilter !== 'all') c.push(where('type', '==', typeFilter));
    c.push(orderBy('createdAt', 'desc'));
    c.push(limit(500));
    return c;
  }, [statusFilter, typeFilter]);
  const { data: reports, loading, error: queryError } =
    useFirestoreCollection(cfg.collection, constraints);
  const directory = useUserDirectory();

  // Switching source resets the filters, because the two collections do not
  // share a status vocabulary ('pending' vs 'Submitted') — carrying the old
  // value over would silently render an empty table.
  const switchSource = (next) => {
    if (next === source) return;
    setSource(next);
    setStatusFilter(SOURCES[next].statusOptions[0].value);
    setTypeFilter('all');
    setSelectedIds([]);
    setSelectedReport(null);
  };

  const { page, pageSize, pageCount, pageData, totalItems, setPage, setPageSize } =
    usePagination(reports, 25);

  useEffect(() => {
    const params = {};
    if (source !== 'moderation') params.source = source;
    if (statusFilter !== 'pending') params.status = statusFilter;
    setSearchParams(params);
  }, [source, statusFilter, setSearchParams]);

  const runAction = async (report, action, notes = '') => {
    try {
      setActionLoading(true);
      if (action === 'dismiss') {
        await dismissReport(report.id, notes);
      } else {
        await moderateReport(report.id, action, notes);
      }
    } catch (err) {
      console.error(err);
      setErrorMsg(`Action failed: ${err.message}`);
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolve = async (action) => {
    if (!selectedReport) return;
    try {
      await runAction(selectedReport, action, resolutionNotes);
      setShowResolveModal(false);
      setShowDetailModal(false);
      setResolutionNotes('');
      setSelectedReport(null);
    } catch {
      // surfaced via errorMsg
    }
  };

  const toggleRow = (id, checked) => {
    setSelectedIds((prev) => (checked ? Array.from(new Set([...prev, id])) : prev.filter((x) => x !== id)));
  };
  const toggleAllOnPage = (checked) => {
    const ids = pageData.map((r) => r.id);
    setSelectedIds((prev) => {
      if (checked) return Array.from(new Set([...prev, ...ids]));
      const drop = new Set(ids);
      return prev.filter((x) => !drop.has(x));
    });
  };
  const clearSelection = () => setSelectedIds([]);

  const bulkDismiss = async () => {
    if (!window.confirm(`Dismiss ${selectedIds.length} reports?`)) return;
    try {
      setActionLoading(true);
      await Promise.all(selectedIds.map((id) => dismissReport(id, 'Bulk dismissed by admin')));
      clearSelection();
    } catch (err) {
      console.error(err);
      setErrorMsg(`Bulk dismiss failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const bulkResolve = async () => {
    if (!window.confirm(`Mark ${selectedIds.length} reports as resolved (no action)?`)) return;
    try {
      setActionLoading(true);
      await Promise.all(selectedIds.map((id) => moderateReport(id, 'resolved', 'Bulk resolved by admin')));
      clearSelection();
    } catch (err) {
      console.error(err);
      setErrorMsg(`Bulk resolve failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'warning', resolved: 'success', dismissed: 'inactive',
      Submitted: 'warning', 'In Progress': 'primary', Closed: 'success',
    };
    return <Badge variant={variants[status] || 'inactive'}>{status || 'unknown'}</Badge>;
  };

  const getTypeBadge = (type) => {
    const variants = {
      user: 'primary', post: 'secondary', business: 'tertiary', message: 'warning',
      EMERGENCY: 'danger', ABUSE: 'danger', NEGLECT: 'warning',
    };
    return <Badge variant={variants[type] || 'inactive'}>{type || 'unknown'}</Badge>;
  };

  const getUrgencyBadge = (urgency) => {
    if (!urgency) return null;
    const variants = { High: 'danger', Medium: 'warning', Low: 'inactive' };
    return <Badge variant={variants[urgency] || 'inactive'}>{urgency}</Badge>;
  };

  const csvColumns = [
    { key: 'id', label: 'Report ID' },
    { key: 'referenceId', label: 'Reference' },
    { key: 'type', label: 'Type' },
    { key: 'urgency', label: 'Urgency' },
    { key: 'reason', label: 'Reason', accessor: (r) => reportReason(r) },
    { key: 'description', label: 'Description' },
    { key: 'location', label: 'Location' },
    { key: 'status', label: 'Status' },
    { key: 'resolution', label: 'Resolution' },
    { key: 'reportedId', label: 'Reported Item ID' },
    { key: 'reportedUserId', label: 'Reported User UID' },
    { key: 'reportedUserName', label: 'Reported User', accessor: (r) => directory.nameOf(r.reportedUserId) },
    { key: 'reporterUid', label: 'Reporter UID', accessor: (r) => reporterUid(r) },
    { key: 'reporterName', label: 'Reporter', accessor: (r) => directory.nameOf(reporterUid(r)) },
    { key: 'reporterEmail', label: 'Reporter Email', accessor: (r) => directory.emailOf(reporterUid(r)) || r.contactEmail || '' },
    { key: 'createdAt', label: 'Created', accessor: (r) => formatDate(r.createdAt) },
    { key: 'resolvedAt', label: 'Resolved', accessor: (r) => formatDate(r.resolvedAt) },
  ];

  const columns = [
    {
      key: 'type',
      title: 'Type',
      render: (value, row) => (
        <div className={styles.typeCell}>
          {getTypeBadge(value || row.postType)}
          {getUrgencyBadge(row.urgency)}
        </div>
      ),
    },
    {
      key: 'reason',
      title: 'Reason',
      render: (_, row) => (
        <span className={styles.reasonCell}>{reportReason(row) || 'No reason provided'}</span>
      ),
    },
    {
      key: 'reportedUser',
      title: 'Reported',
      render: (_, row) => {
        // Moderation reports name a target; welfare reports name a place.
        if (row.reportedUserId || row.reportedId) {
          const uid = row.reportedUserId || row.reportedId;
          return (
            <div className={styles.stack}>
              <span className={styles.strong}>{directory.nameOf(uid) || uid}</span>
              <span className={styles.meta}>{row.postType || row.type || ''}</span>
            </div>
          );
        }
        return <span className={styles.meta}>{row.location || '—'}</span>;
      },
    },
    {
      key: 'reportedBy',
      title: 'Reported By',
      render: (_, row) => {
        const uid = reporterUid(row);
        if (!uid) return 'Anonymous';
        return (
          <div className={styles.stack}>
            <span className={styles.strong}>{directory.nameOf(uid) || uid}</span>
            <span className={styles.meta}>
              {directory.emailOf(uid) || row.contactEmail || directory.handleOf(uid) || uid}
            </span>
          </div>
        );
      },
    },
    { key: 'status', title: 'Status', render: (value) => getStatusBadge(value) },
    { key: 'createdAt', title: 'Date', render: (value) => formatDate(value) },
    {
      key: 'actions',
      title: '',
      width: '160px',
      render: (_, row) => (
        <div className={styles.actions}>
          <Button
            variant="ghost"
            size="sm"
            icon={<FiEye />}
            title="View"
            onClick={() => {
              setSelectedReport(row);
              setShowDetailModal(true);
            }}
          />
          <Button
            variant="ghost"
            size="sm"
            icon={<FiFileText />}
            title="Download PDF"
            onClick={() => exportReportToPdf(row, user || {})}
          />
          {/* adminModerateReport only knows `content_reports` ids, so the
              moderation buttons are hidden on the welfare feed rather than
              offered and then failing with not-found. */}
          {cfg.actionable && row.status === 'pending' && (
            <>
              <Button
                variant="primary"
                size="sm"
                icon={<FiCheck />}
                title="Resolve"
                onClick={() => {
                  setSelectedReport(row);
                  setShowResolveModal(true);
                }}
                disabled={actionLoading}
              />
              <Button
                variant="danger"
                size="sm"
                icon={<FiX />}
                title="Dismiss"
                onClick={() => runAction(row, 'dismiss', 'Dismissed without action')}
                disabled={actionLoading}
              />
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>User Reports</h1>
          {!loading && (
            <Badge variant="danger">
              {reports.filter((r) => r.status === 'pending' || r.status === 'Submitted').length} open
            </Badge>
          )}
        </div>
        <p className={styles.subtitle}>
          {source === 'moderation'
            ? 'Reported posts and profiles — the queue admin actions apply to'
            : 'Animal welfare and emergency case files filed from the app'}
          <span className={styles.liveText}> Live</span>
        </p>
      </div>

      {queryError && (
        <Card>
          <div style={{ color: '#f87171' }}>
            Could not read <code>{cfg.collection}</code>: {queryError.message}
          </div>
        </Card>
      )}
      {errorMsg && (
        <Card>
          <div style={{ color: '#f87171' }}>{errorMsg}</div>
        </Card>
      )}

      <Card>
        <div className={styles.toolbar}>
          <div className={styles.filters}>
            {Object.entries(SOURCES).map(([key, s]) => (
              <button
                key={key}
                className={`${styles.sourceBtn} ${source === key ? styles.active : ''}`}
                onClick={() => switchSource(key)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.toolbar}>
          <div className={styles.filters}>
            {cfg.statusOptions.map((option) => (
              <button
                key={option.value}
                className={`${styles.filterBtn} ${statusFilter === option.value ? styles.active : ''}`}
                onClick={() => setStatusFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className={styles.filters}>
            {cfg.typeOptions.map((option) => (
              <button
                key={option.value}
                className={`${styles.filterBtn} ${typeFilter === option.value ? styles.active : ''}`}
                onClick={() => setTypeFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <CsvExportButton
            rows={reports}
            columns={csvColumns}
            filename={`dierend-${source}-reports-${statusFilter}`}
          />
        </div>

        {cfg.actionable && (
          <BulkActionBar selectedCount={selectedIds.length} onClear={clearSelection}>
            <Button variant="primary" size="sm" onClick={bulkResolve} disabled={actionLoading}>
              Mark Resolved
            </Button>
            <Button variant="danger" size="sm" onClick={bulkDismiss} disabled={actionLoading}>
              Dismiss Selected
            </Button>
          </BulkActionBar>
        )}

        <Table
          columns={columns}
          data={pageData}
          loading={loading}
          emptyMessage={`No ${cfg.label.toLowerCase()} reports found`}
          selectable={cfg.actionable}
          selectedIds={selectedIds}
          onToggleRow={toggleRow}
          onToggleAll={toggleAllOnPage}
        />

        <Pagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Card>

      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="Report Details"
        size="lg"
        footer={
          selectedReport && (
            <>
              <Button variant="ghost" onClick={() => setShowDetailModal(false)}>
                Close
              </Button>
              <Button
                variant="ghost"
                icon={<FiFileText />}
                onClick={() => exportReportToPdf(selectedReport, user || {})}
              >
                Download PDF
              </Button>
              {cfg.actionable && selectedReport.status === 'pending' && (
                <>
                  <Button variant="danger" onClick={() => handleResolve('dismiss')}>
                    Dismiss
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => {
                      setShowDetailModal(false);
                      setShowResolveModal(true);
                    }}
                  >
                    Take Action
                  </Button>
                </>
              )}
            </>
          )
        }
      >
        {selectedReport && (
          <div className={styles.detailContent}>
            <div className={styles.reportHeader}>
              <div className={styles.reportIcon}>
                <FiAlertTriangle />
              </div>
              <div>
                <h3>{reportReason(selectedReport) || 'Report'}</h3>
                <div className={styles.reportMeta}>
                  {getTypeBadge(selectedReport.type || selectedReport.postType)}
                  {getStatusBadge(selectedReport.status)}
                  {getUrgencyBadge(selectedReport.urgency)}
                </div>
              </div>
            </div>

            <div className={styles.detailGrid}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Reporter</span>
                <span className={styles.detailValue}>
                  {directory.nameOf(reporterUid(selectedReport)) || 'Anonymous'}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Reporter email</span>
                <span className={styles.detailValue}>
                  {directory.emailOf(reporterUid(selectedReport)) || selectedReport.contactEmail || '-'}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Reporter UID</span>
                <span className={styles.detailValue}>{reporterUid(selectedReport) || '-'}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Reported On</span>
                <span className={styles.detailValue}>{formatDate(selectedReport.createdAt)}</span>
              </div>
              {selectedReport.referenceId && (
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Reference</span>
                  <span className={styles.detailValue}>{selectedReport.referenceId}</span>
                </div>
              )}
              {selectedReport.location && (
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Location</span>
                  <span className={styles.detailValue}>{selectedReport.location}</span>
                </div>
              )}
              {selectedReport.coordinates && (
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Coordinates</span>
                  <span className={styles.detailValue}>
                    <a
                      href={`https://www.google.com/maps?q=${selectedReport.coordinates.latitude},${selectedReport.coordinates.longitude}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {selectedReport.coordinates.latitude?.toFixed?.(5)},{' '}
                      {selectedReport.coordinates.longitude?.toFixed?.(5)}
                    </a>
                  </span>
                </div>
              )}
              {selectedReport.recipientName && (
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Sent to</span>
                  <span className={styles.detailValue}>
                    {selectedReport.recipientName}
                    {selectedReport.recipient ? ` (${selectedReport.recipient})` : ''}
                  </span>
                </div>
              )}
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Reported Item ID</span>
                <span className={styles.detailValue}>
                  {selectedReport.reportedId || selectedReport.postId || '-'}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Reported User</span>
                <span className={styles.detailValue}>
                  {selectedReport.reportedUserId
                    ? directory.nameOf(selectedReport.reportedUserId) || selectedReport.reportedUserId
                    : '-'}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Resolution</span>
                <span className={styles.detailValue}>{selectedReport.resolution || '-'}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Resolved</span>
                <span className={styles.detailValue}>{formatDate(selectedReport.resolvedAt)}</span>
              </div>
            </div>

            {selectedReport.description && (
              <div className={styles.description}>
                <h4>Description</h4>
                <p>{selectedReport.description}</p>
              </div>
            )}

            {Array.isArray(selectedReport.images) && selectedReport.images.length > 0 && (
              <div className={styles.description}>
                <h4>Photos ({selectedReport.images.length})</h4>
                <div className={styles.imageGrid}>
                  {selectedReport.images.map((url, i) => (
                    <a key={`${url}-${i}`} href={url} target="_blank" rel="noreferrer">
                      <img src={url} alt="" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {selectedReport.pdfReport && (
              <div className={styles.description}>
                <h4>Filed PDF</h4>
                <a href={selectedReport.pdfReport} target="_blank" rel="noreferrer">
                  Open the report PDF that was emailed to {selectedReport.recipientName || 'the recipient'}
                </a>
              </div>
            )}

            {selectedReport.resolutionNotes && (
              <div className={styles.resolution}>
                <h4>Resolution Notes</h4>
                <p>{selectedReport.resolutionNotes}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={showResolveModal}
        onClose={() => {
          setShowResolveModal(false);
          setResolutionNotes('');
        }}
        title="Resolve Report"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowResolveModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => handleResolve('remove_content')} disabled={actionLoading}>
              Remove Content
            </Button>
            <Button variant="danger" onClick={() => handleResolve('ban_user')} disabled={actionLoading}>
              Ban User
            </Button>
            <Button variant="warning" onClick={() => handleResolve('warn_user')} disabled={actionLoading}>
              Issue Warning
            </Button>
            <Button variant="primary" onClick={() => handleResolve('resolved')} loading={actionLoading}>
              Mark Resolved
            </Button>
          </>
        }
      >
        <p className={styles.resolveText}>Choose an action for this report:</p>
        <Input
          placeholder="Add resolution notes (optional)..."
          value={resolutionNotes}
          onChange={(e) => setResolutionNotes(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default Reports;
